import {
  catalogVolumeToBodymap,
  DAY_MS,
  detectRegression,
  normalizeVolumeByMuscle,
  repsAtLoadBySlug,
  rollingWindowStart,
  strongerPain,
  volumeByPattern,
  worstPainSeverity,
  type Exercise,
  type LogSet,
  type MovementPattern,
  type MuscleSlug,
  type PainMap,
  type PainSeverity,
  type RegressionResult,
  type RepsAtLoadPoint,
  type TimedSessionResult,
} from "@heraion/shared";

// Pure aggregation behind the Tier-1 progress panels (lifted from mobile
// with the panels). The panels keep the data hook, i18n, theme and
// render; the windowing / ranking / regression math lives here so it can
// be unit-tested.

// =========================================================================
// MovementBalancePanel — sets per movement pattern, headline window + 7d inset
// =========================================================================
// PG-1 period selector: `windowDays` drives the headline (was fixed 28d); the
// small "X/7d" inset stays a fixed 7-day recent-push reference regardless of
// the selected window. Default 28 keeps the pre-selector behaviour identical.
export type MovementBalanceRow = {
  pattern: MovementPattern;
  setsWindow: number;
  sets7d: number;
  volumeWindowKg: number;
};

export function computeMovementBalance(
  results: TimedSessionResult[],
  catalog: ReadonlyArray<Exercise>,
  nowMs: number,
  windowDays = 28,
): MovementBalanceRow[] {
  const byWindow = volumeByPattern(results, catalog, nowMs, windowDays);
  const by7d = volumeByPattern(results, catalog, nowMs, 7);
  return (Object.keys(byWindow) as MovementPattern[])
    .map((p) => ({
      pattern: p,
      setsWindow: byWindow[p].sets,
      sets7d: by7d[p].sets,
      volumeWindowKg: byWindow[p].volumeKg,
    }))
    .filter((r) => r.setsWindow > 0)
    .sort((a, b) => b.setsWindow - a.setsWindow);
}

// =========================================================================
// PainVolumePanel — per-muscle pain × recent volume snapshot
// =========================================================================
// PG-1 period selector: `windowDays` drives the headline volume (was 28d); the
// "X / 7d" inset stays a fixed 7-day recent reference. Default 28 keeps the
// pre-selector behaviour identical.
export type PainVolumeRow = {
  muscleSlug: MuscleSlug;
  severity: PainSeverity;
  volume7dKg: number;
  volumeWindowKg: number;
};

/** The muscles the pain map flags, each at its worse side (PG-14): volume
 *  has no side. Empty means the Pain × volume panel has nothing to show,
 *  which Progress knows before any fetch (PG-57). */
export function flaggedPainMuscles(
  painMap: PainMap,
): Array<readonly [MuscleSlug, PainSeverity]> {
  return Object.entries(painMap).flatMap(([slug, entry]) => {
    const sev = worstPainSeverity(entry);
    return sev ? [[slug as MuscleSlug, sev] as const] : [];
  });
}

export function computePainVolume(
  results: TimedSessionResult[],
  painMap: PainMap,
  nowMs: number,
  windowDays = 28,
  // PG-64: rows are merged by the label the panel SHOWS (abs and obliques are
  // both "Core"): worst severity, summed volume. Default = one row per slug.
  labelOf: (slug: MuscleSlug) => string = (slug) => slug,
): PainVolumeRow[] {
  const muscles = flaggedPainMuscles(painMap);
  if (muscles.length === 0) return [];
  const cutoff7d = rollingWindowStart(nowMs, 7);
  const cutoffWindow = rollingWindowStart(nowMs, windowDays);
  const out: PainVolumeRow[] = [];
  for (const [slug, sev] of muscles) {
    let v7 = 0;
    let vWindow = 0;
    for (const ts of results) {
      const ms = ts.startedAtMs;
      if (ms < cutoffWindow || ms > nowMs) continue;
      // volumeByMuscle is catalog vocab. The pain picker works in bodymap
      // vocab (anatomical regions), so bridge the catalog map back through
      // catalogVolumeToBodymap before the bodymap-slug lookup.
      // normalizeVolumeByMuscle handles legacy bodymap-keyed rows.
      const bodymapVolume = catalogVolumeToBodymap(
        normalizeVolumeByMuscle(ts.result.volumeByMuscle),
      );
      const m = bodymapVolume[slug] ?? 0;
      if (m <= 0) continue;
      if (ms >= cutoff7d) v7 += m;
      vWindow += m;
    }
    out.push({
      muscleSlug: slug,
      severity: sev,
      volume7dKg: v7,
      volumeWindowKg: vWindow,
    });
  }
  const merged = new Map<string, PainVolumeRow>();
  for (const row of out) {
    const key = labelOf(row.muscleSlug);
    const prev = merged.get(key);
    merged.set(
      key,
      prev
        ? {
            ...prev,
            severity: strongerPain(prev.severity, row.severity),
            volume7dKg: prev.volume7dKg + row.volume7dKg,
            volumeWindowKg: prev.volumeWindowKg + row.volumeWindowKg,
          }
        : row,
    );
  }
  return [...merged.values()].sort((a, b) => b.volume7dKg - a.volume7dKg);
}

// =========================================================================
// RepsAtLoadTrendsPanel — the SECOND strength axis (measurement-contract §8)
// =========================================================================
// The e1RM axis above ranks ONE lift per movement pattern and excludes
// isolation/machine work (a regression alert on a curl isn't actionable). This
// axis is exactly its complement: it lists exercises DIRECTLY (no pattern
// grouping, no exclusions) and lives for the high-rep, machine, feel-based work
// that never grows an e1RM series. "At the load you keep returning to, are the
// reps climbing at equal-or-better effort?" — a directly observed gain.

// Progress is being slimmed (measurement-contract §1); cap the row count so
// this never becomes a wall. Measured rows win the slots; the honest
// "insufficient" nudges fill whatever remains.
export const REPS_AT_LOAD_MAX_ROWS = 6;

export type RepsAtLoadEntry = {
  slug: string;
  snapshot: string | undefined;
  loadKg: number;
  /** Effort-tagged points at the fixed load, chronological — the comparable
   *  series the trend is fit on (reps at an unknown effort aren't comparable). */
  sorted: RepsAtLoadPoint[];
  /** Reps of the most recent headline set — the number the row leads with. */
  latestReps: number;
  latestAtMs: number;
  reg: RegressionResult;
  /**
   * §3 "measured" vs "insufficient": ≥2 effort-tagged days at the fixed load,
   * so a "then→now" comparison at equal-or-better effort actually exists. When
   * false the load recurs but its sets aren't effort-tagged — the row shows the
   * honest sentence naming the gesture, not a trend.
   */
  measured: boolean;
};

export function rankRepsAtLoad(
  sets: readonly LogSet[],
  nowMs: number,
  windowDays = 90,
): RepsAtLoadEntry[] {
  const cutoffMs = nowMs - windowDays * DAY_MS;
  const entries: RepsAtLoadEntry[] = [];
  for (const [slug, series] of repsAtLoadBySlug(sets, {
    fromMs: cutoffMs,
    toMs: nowMs,
  })) {
    const effortPts = series.points.filter((p) => p.rir != null);
    const measured = series.comparableDays >= 2;
    // Trend fit on the effort-tagged points only. `kgPerRep1` is
    // detectRegression's generic y-slot — here it carries reps. `rir` feeds the
    // same effort-aware downgrade the e1RM axis uses (a reps drop taken easy
    // isn't proven weakness), reused verbatim rather than re-derived.
    const reg = detectRegression(
      effortPts.map((p) => ({ atMs: p.atMs, kgPerRep1: p.reps, rir: p.rir })),
      nowMs,
      windowDays / 7,
    );
    // Effort-tagged points headline a measured row; an insufficient row falls
    // back to its raw points so it can still say "you keep training this load".
    const headline = effortPts.length > 0 ? effortPts : series.points;
    const last = headline[headline.length - 1]!;
    entries.push({
      slug,
      snapshot: series.snapshot,
      loadKg: series.loadKg,
      sorted: effortPts,
      latestReps: last.reps,
      latestAtMs: last.atMs,
      reg,
      measured,
    });
  }
  // Measured first (richer comparison, then more recent); insufficient nudges
  // take the leftover slots, most recent first.
  entries.sort((a, b) => {
    if (a.measured !== b.measured) return a.measured ? -1 : 1;
    if (a.measured && b.sorted.length !== a.sorted.length) {
      return b.sorted.length - a.sorted.length;
    }
    return b.latestAtMs - a.latestAtMs;
  });
  return entries.slice(0, REPS_AT_LOAD_MAX_ROWS);
}
